'use client';
import { useTranslations } from 'next-intl';
import { CatHeading } from 'components/blocks-components';
import { replaceTokensWithComponents } from '@cat-ecom/common/utils/stringUtils';
import HeightLimitedExpander from 'components/height-limited-expander';
import AnchorMenuMobileSectionHeader from '../anchor-menu-mobile/anchor-menu-mobile-section-header/anchor-menu-mobile-section-header';
import { capitalizeText, isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { Conditional } from '@cat-ecom/pcc-components';
import { useEffect, useRef, useState } from 'react';
import { MAX_HEIGHT_FOR_COMPATIBLE_MODELS } from '../../../constants/constants';
import styles from './anchor-menu-compatible.module.scss';

const AnchorMenuCompatible = ({
  data,
  isMobile = false,
  isPCC_PDP_headerUpdateMobileView = false
}) => {
  const t = useTranslations();
  const valuesRef = useRef(null);
  const [isOverFlowing, setIsOverFlowing] = useState(false);

  const desktopTitle = replaceTokensWithComponents(
    t('PDP_COMPATIBLE_MODELS', { '0': '{0}' }),
    data.partNumber
  );

  const compatibleModels: { [key: string]: string[] } =
    data?.compatibleModels || {};
  const [title = '', models = ['']]: [string, string[]] = Object.entries(
    compatibleModels
  )[0] || ['', ['']];

  useEffect(() => {
    const checkOverFlow = () => {
      if (valuesRef.current) {
        setIsOverFlowing(
          valuesRef.current?.scrollHeight > MAX_HEIGHT_FOR_COMPATIBLE_MODELS
        );
      }
    };
    checkOverFlow();
    const resizeObserver = new ResizeObserver(checkOverFlow);
    if (valuesRef.current) {
      resizeObserver.observe(valuesRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [models]);
  const shouldhideButton =
    isEmpty(compatibleModels) ||
    (Object.entries(compatibleModels).length < 2 && !isOverFlowing);
  return (
    <>
      <Conditional
        test={isMobile}
        fallback={<CatHeading variant="title" className='cat-u-theme-typography-title'>{desktopTitle}</CatHeading>}
      >
        <div className="mb-3">
          <AnchorMenuMobileSectionHeader
            titleText={t('COMPATIBLE_MODELS')}
            shouldShowButton={
              isPCC_PDP_headerUpdateMobileView ? !shouldhideButton : false
            }
          >
            {Object.entries(compatibleModels).map(([key, models]) => (
              <div className="mb-4" key={key}>
                <div
                  className={
                    'no-equipment-image d-flex gap-1 align-items-center'
                  }
                >
                  <CatHeading variant="display-sm">
                    {capitalizeText(key)}
                  </CatHeading>
                </div>
                <div className="d-flex gap-3 flex-wrap">
                  {models.map((value) => (
                    <CatHeading key={value} variant="body-sm">
                      {value}
                    </CatHeading>
                  ))}
                </div>
              </div>
            ))}
          </AnchorMenuMobileSectionHeader>
        </div>
      </Conditional>
      {data?.compatibleModels ? (
        <div className="cat-u-margin-top-md">
          <HeightLimitedExpander
            buttonSize="md"
            id="compatibleExpander"
            className="cat-u-theme-typography-body-sm"
            lessButtonText={t('VIEW_LESS')}
            moreButtonText={t('VIEW_MORE_PRODUCTS')}
          >
            <Conditional test={!isPCC_PDP_headerUpdateMobileView || !isMobile}>
              {Object.entries(compatibleModels).map(([key, value]) => (
                <div key={key}>
                  <p className="cat-u-margin-bottom-none cat-u-theme-typography-label-lg">
                    {key}
                  </p>
                  <p className="cat-u-theme-typography-body">
                    {compatibleModels[key].join(' ')}
                  </p>
                </div>
              ))}
            </Conditional>
          </HeightLimitedExpander>
          <Conditional test={isPCC_PDP_headerUpdateMobileView && isMobile}>
            <div>
              <div
                className={'no-equipment-image d-flex gap-1 align-items-center'}
              >
                <CatHeading variant="display-sm">
                  {capitalizeText(title)}
                </CatHeading>
              </div>
              <div ref={valuesRef} className={styles['model_lines_truncate']}>
                {models.map((value) => (
                  <CatHeading key={value} variant="body-sm">
                    {value}
                  </CatHeading>
                ))}
              </div>
            </div>
          </Conditional>
        </div>
      ) : (
        <p className="cat-u-margin-bottom-none cat-u-theme-typography-body mt-3">
          {t('PDP_COMPATIBILITY_UNAVAILABLE')}
        </p>
      )}
    </>
  );
};

export default AnchorMenuCompatible;
